import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import Swal from 'sweetalert2'
import moment from 'moment'
import { editFaqs, viewFaqDetails } from '../../slice/faqSlice'

// Validation Schema
const editFaqSchema = yup.object().shape({
    category: yup
        .string()
        .required('Category is required')
        .min(2, 'Category must be at least 2 characters')
        .max(100, 'Category cannot exceed 100 characters'),

    question: yup
        .string()
        .required('Question (English) is required')
        .min(5, 'Minimum 5 characters')
        .max(500),

    answer: yup
        .string()
        .required('Answer (English) is required')
        .min(5),

    question_ar: yup.string().nullable(),
    answer_ar: yup.string().nullable(),

    is_active: yup.boolean()
})

const EditFaq = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()

    const faqId = location.state?.id
    const { faqDetail, loading } = useSelector(state => state.faq)

    const [isSubmitting, setIsSubmitting] = useState(false)

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue
    } = useForm({
        resolver: yupResolver(editFaqSchema),
        defaultValues: {
            category: '',
            question: '',
            answer: '',
            question_ar: '',
            answer_ar: '',
            is_active: true
        }
    })

    // Fetch FAQ details
    useEffect(() => {
        if (!faqId) {
            navigate('/faq')
            return
        }
        dispatch(viewFaqDetails({ id: faqId }))
    }, [faqId, dispatch, navigate])

    // Populate form
    useEffect(() => {
        if (faqDetail) {
            setValue('category', faqDetail.category || '')      
            setValue('question', faqDetail.question || '')
            setValue('answer', faqDetail.answer || '')
            setValue('question_ar', faqDetail.question_ar || '')
            setValue('answer_ar', faqDetail.answer_ar || '')
            setValue('is_active', faqDetail.is_active === 1)
        }
    }, [faqDetail, setValue])

    const onSubmit = async (data) => {
        setIsSubmitting(true)

        try {
            const payload = {
                id: faqId,
                category: data.category,
                question: data.question,
                answer: data.answer,
                question_ar: data.question_ar || null,
                answer_ar: data.answer_ar || null,
                is_active: data.is_active ? 1 : 0
            }

            const response = await dispatch(editFaqs(payload))

            if (response?.payload?.code === 200) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'FAQ updated successfully',
                    timer: 2000,
                    showConfirmButton: false
                })

                setTimeout(() => navigate('/faq'), 2000)
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response?.payload?.message || 'Failed to update FAQ'
                })
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.message || 'Something went wrong'
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    // Loading State
    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary"></div>
            </div>
        )
    }

    // No Data
    if (!faqDetail) {
        return (
            <div className="text-center py-5">
                <h5>No FAQ found</h5>
                <button className="btn btn-primary mt-3" onClick={() => navigate('/faq')}>
                    Back to FAQ List
                </button>
            </div>
        )
    }

    return (
        <div className="container-fluid px-4 py-3">

            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-semibold mb-0">Edit FAQ</h4>
                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/faq')}
                >
                    ← Back to List
                </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="card border-0 shadow-sm mb-4">
                    <div className="card-header bg-light fw-semibold">
                        FAQ Details
                    </div>

                    <div className="card-body p-4">
                        <div className="row g-3">

                            {/* Category */}
                            <div className="col-12">
                                <label className="form-label">
                                    Category <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.category ? 'is-invalid' : ''}`}
                                    {...register('category')}
                                />
                                {errors.category && (
                                    <div className="invalid-feedback">{errors.category.message}</div>
                                )}
                            </div>

                            {/* Question EN */}
                            <div className="col-12">
                                <label className="form-label">
                                    Question (English) <span className="text-danger">*</span>
                                </label>
                                <textarea
                                    className={`form-control ${errors.question ? 'is-invalid' : ''}`}
                                    rows="2"
                                    {...register('question')}
                                />
                                {errors.question && (
                                    <div className="invalid-feedback">{errors.question.message}</div>
                                )}
                            </div>

                            {/* Answer EN */}
                            <div className="col-12">
                                <label className="form-label">
                                    Answer (English) <span className="text-danger">*</span>
                                </label>
                                <textarea
                                    className={`form-control ${errors.answer ? 'is-invalid' : ''}`}
                                    rows="3"
                                    {...register('answer')}
                                />
                                {errors.answer && (
                                    <div className="invalid-feedback">{errors.answer.message}</div>
                                )}
                            </div>

                            {/* Question AR */}
                            <div className="col-12">
                                <label className="form-label">Question (Arabic)</label>
                                <textarea
                                    className="form-control"
                                    rows="2"
                                    dir="rtl"
                                    {...register('question_ar')}
                                />
                            </div>

                            {/* Answer AR */}
                            <div className="col-12">
                                <label className="form-label">Answer (Arabic)</label>
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    dir="rtl"
                                    {...register('answer_ar')}
                                />
                            </div>

                            {/* Status */}
                            <div className="col-12">
                                <div className="form-check form-switch">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        {...register('is_active')}
                                    />
                                    <label className="form-check-label">
                                        Active
                                    </label>
                                </div>
                            </div>

                            {/* Info */}
                            <div className="col-12 mt-3">
                                <small className="text-muted">
                                    Created: {faqDetail?.createdAt ? moment(faqDetail.createdAt).format('DD MMM YYYY, hh:mm A') : 'NA'}
                                </small>
                                <br />
                                <small className="text-muted">
                                    Updated: {faqDetail?.updatedAt ? moment(faqDetail.updatedAt).format('DD MMM YYYY, hh:mm A') : 'NA'}
                                </small>
                            </div>

                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="d-flex gap-3 justify-content-end">
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => navigate('/faq')}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>

                    <button
                        type="submit"
                        className="btn btn-success"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Updating...' : 'Update FAQ'}
                    </button>
                </div>
            </form>
        </div>
    )
}

export default EditFaq